import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';

import {TokenService} from './token.service';

import {COTLangParser} from '../../../src/COTLangParser';
import {COTLangSupport} from '../../../src/support/COTLangSupport';
import {Subscription} from 'rxjs';
import {IToken} from 'ebnf';
import {FunctionsCode} from '../../../src/functions/functions.code';

/// CALCULATE $CODE VARIANTS
const validFunctions = [];
const functionMap = new FunctionsCode(null).functionMap;
const functionMapModel = Object.keys(functionMap);
functionMapModel.forEach(x => {
  const functionMapModelVariant = Object.keys(functionMap[x]);
  if (functionMapModelVariant.length) {
    functionMapModelVariant.forEach(y => {
      validFunctions.push(`$CODE#${x}#${y}#${Array(functionMap[x][y].length - 1).fill(1).map((_, i) => `<arg_${i + 1}>`).join('#')}`);
    });
  }
});
/// ENDOF $CODE VARIANTS

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private tokenService: TokenService,
    private snackBar: MatSnackBar,
  ) {}

  title = 'cotLang';
  snackBarTime = 5000;  // milliseconds
  cotLangValueExample = '{ "hola" : { "mundo": { "hello": "hello" } } }';
  cotLangOuputExample = '[  {  "key": "step1", "result": {  "a": 1 } } ]';
  cotLangResponse = '';

  contexts: { display: string, value: string }[] = [];

  emailForm = this.fb.group({
    email: ['', [Validators.email, Validators.required]],
    password: ['', [Validators.required]],
  });

  tokenForm = new FormControl();

  cotLangForm = this.fb.group({
    expression: ['(($JOIN#-#\\[#($VALUE#a)#\\]#```(World)```#(\\[Escape\\]|[querystring=>escape])))', [Validators.required]],
    value: [JSON.stringify({ a: 'Hello'}), [Validators.required]],
    context: [''],
    output: ['[]', [Validators.required]],
  });

  private subscriptions = new Subscription();

  examples = [
    { input: 'Hello World!', result: '', notes: 'Simple literal with no special characters.' },
    { input: 'Hello \\( World!', result: '', notes: 'Special chars as ( MUST be escaped.' },
    { input: '(Hello World!)', result: '', notes: 'Parenthesis are used to separate contexts.' },
    { input: '```(Hello World!)```', result: '', notes: 'Everything inside triple ` is a literal.' },
    { input: '$VALUE#a|b|en', result: '', value: { a: { b: { en: 'Hello', es: 'Hola' } } } , notes: '$VALUE extracts data from context' },
    { input: '$VALUE#a|b|es', result: '', value: { a: { b: { en: 'Hello', es: 'Hola' } } } , notes: '$VALUE arguments define the path' },
    { input: '$JOIN#---#cotalker#language#examples', result: '' , notes: '$JOIN converts and concatenates every argument into a string, joined by the first argument'  },
    { input: '$CODE#users#email#john.doe@cotalker.com', result: { _id: '1234', email: 'john.doe@cotalker.com', name: { names : 'John' } },
      notes: '$CODE looks up data from different internal source. In this case searches for an user with this email' },
    { input: '$OUTPUT#english#a', result: '', output: [
      { key: 'english', result: { a: 'Hello!' } },
      { key: 'spanish', result: { a: '¡Hola!' } },
    ], notes: '$OUTPUT, as $VALUE reads the outputs array using the first argument to find the key. After this behaves as $VALUE' },
    { input: '$OUTPUT#spanish#a', result: '', output: [
        { key: 'english', result: { a: 'Hello!' } },
        { key: 'spanish', result: { a: '¡Hola!' } },
    ], notes: 'In this case the first argument is extracting the second array element' },
    { input: '$$TIME#years#1 ', notes: '$$TIME generates relatives dates in mS from epoc (unix-time * 1000), This same day the next Year'},
    { input: '$$TIME#days#-1 ', notes: 'This will generate Yesterday at this same time'},
    { input: '$ENV#BASEURL', notes: 'Converts $ENV into environment variable. BASEURL holds the current API base location'},

    { input: '$VALUE#a|[size=>*]', value: { a: ['cat', 'dog', 'zebra', 'mouse'] }, notes: 'function size returns the length of thr array' },
    { input: '$VALUE#a|[size=>*]', value: { a: 'mouse' }, notes: 'function size return the length of the string' },
    { input: '$VALUE#a|[toString=>___]', value: { a: ['cat', 'dog', 'zebra', 'mouse'] } },
    { input: '$VALUE#a|[simpleNotEqualFilter=>Apple]', value: { a: ['Apple', 'Orange', 'Apple', 'Pineapple'] } },
    { input: '$VALUE#a|[simpleEqualFilter=>Apple]', value: { a: ['Apple', 'Orange', 'Apple', 'Pineapple'] } },
    { input: '$VALUE#a|[filterIndex=>Apple]', value: { a: ['Apple', 'Orange', 'Apple', 'Pineapple'] } },
    { input: '$VALUE#a|[concat=>array=($VALUE#b)]', value: { a: ['Apple', 'Orange', 'Apple', 'Pineapple'], b: ['Strawberry'] } },
    { input: '$VALUE#a|[push=>string=Strawberry]|[push=>string=Strawberry]', value: { a: ['Apple', 'Orange', 'Apple', 'Pineapple'] } },
    { input: '$VALUE#a|[add=>string=Strawberry]|[add=>string=Strawberry]', value: { a: ['Apple', 'Orange', 'Apple', 'Pineapple'] } },

    { input: '$VALUE#a|[cast=>parseInt]', value: { a: '19.4932' } },
    { input: '$VALUE#a|[math=>add=10]', value: { a: 100 } },
    { input: '$VALUE#a|[date=>format=DD-MM-YYYY@America/Santiago]', value: { a: new Date() } },

    { input: '$VALUE#a|[every=>gte=10]', value: { a: [0, 10, 20, 30, 40, 50] } },
    { input: '$VALUE#a|[some=>gte=10]', value: { a: [0, 10, 20, 30, 40, 50] } },
    { input: '$VALUE#a|[json=>parse]', value: { a: '{ "hello":"world" }' } },
    { input: '$VALUE#a|[querystring=>*]', value: { a: { value: 'Sir Edvárd' } } },
    { input: '$VALUE#a|[cotanswer_list=>array]',
      value: { a: { responses: ['__ON__', '__OFF__', '__ON__'], display: ['a', 'b', 'c'],  }  } },
    { input: '$VALUE#a|[gencode=>*]', value: { a: 'test' } },

    { input: '$VALUE#a|[map=>age]',
      value: { a: [{ age: 20, name: 'John' }, { age: 32, name: 'Jane' }, { name: 'Mr. Ed' } ] } },
    { input: '$VALUE#a|[arrayToObject=>name=age]',
      value: { a: [{ age: 20, name: 'John' }, { age: 32, name: 'Jane' }, { name: 'Mr. Ed' } ] } },
    { input: '$VALUE#a|[isDefinedFilter=>age]',
      value: { a: [{ age: 20, name: 'John' }, { age: 32, name: 'Jane' }, { name: 'Mr. Ed' } ] } },
    { input: '$VALUE#a|[filter=>age=32]',
      value: { a: [{ age: 20, name: 'John' }, { age: 32, name: 'Jane' }, { name: 'Mr. Ed' } ] } },
    { input: '$VALUE#a|[notEqualFilter=>name=Jane]',
      value: { a: [{ age: 20, name: 'John' }, { age: 32, name: 'Jane' }, { name: 'Mr. Ed' } ] } },
    { input: '$VALUE#a|[find=>name=Jane]',
      value: { a: [{ age: 20, name: 'John' }, { age: 32, name: 'Jane' }, { name: 'Mr. Ed' } ] } },
  ];

  documentation = [
    { name: 'parenthesis',
      type: 'syntax',
      description: [
        'Parenthesis allows to separate elements.',
        'They do not have any effect over the data.',
      ],
      arguments: '(any)' },
    { name: 'pipes',
      type: 'syntax',
      description: [
        'Pipes allows to navigate into objects and arrays though their keys.',
        'Arguments are divided by |',
      ],
      arguments: 'a|b|c|...|n' },
    { name: 'literals (escaped)',
      type: 'syntax',
      description: [
        'Any literal string, special chars: | ( ) [ ] # must be escaped',
      ],
      arguments: 'string \\(' },
    { name: 'literals (triple `)',
      type: 'syntax',
      description: [
        'Any literal string, only ` must be escaped',
      ],
      arguments: '```string```' },
    { name: '$JOIN',
      type: 'command',
      description: [
        'Command $JOIN has 2 or more arguments. The first is the divider.',
        'The next N arguments are concatenated into a string divided by the divider',
      ],
      arguments: '$JOIN#divider#first#second#third#...#n' },
    { name: '$CODE',
      type: 'command',
      description: [
        'Command $CODE has 2, 3, 4 or 5 arguments.',
        'The first is the model, the second the variant and the rest is the target.',
        'Valid arguments:',
      ].concat(validFunctions),
      arguments: '$CODE#model#variant#target_1#target_2#target_3' },
    { name: '$VALUE',
      type: 'command',
      description: [
        'Command $VALUE extracts data from the context.',
        'Has exactly one argument that defines the pipe',
      ],
      arguments: '$VALUE#a|b|c' },
    { name: '$OUTPUT',
      type: 'command',
      description: [
        'Command $OUTPUT extracts data from the outputs.',
        'Has exactly two arguments. The first is the output name and the second the extraction pipe.',
      ],
      arguments: '$OUTPUT#ouput-name#a|b|c' },
    { name: '$$TIME',
      type: 'command',
      description: [
        'Command $$TIME generates a number in milliseconds from epoc (unix time * 1000) from now.',
        'Valid "interval" years, quarters, months, weeks, days, hours, minutes, seconds, milliseconds.',
        '"value" is a number, e.g., -1, 24',
      ],
      arguments: '$$TIME#interval#value' },
    { name: '$ENV',
      type: 'command',
      description: [
        'Command $ENV gets environment variables.',
        'Valid environment_variable_name:',
        'BASEURL: Current api location, useful for multi-environment scripts',
      ],
      arguments: '$ENV#environment_variable_name' },
    { name: 'size',
      type: 'function',
      description: [
        'Function size returns size of array or string.',
        'Arguments:',
        'a: "*" No effect',
      ],
      arguments: '[size=>a]' },
    { name: 'toString',
      type: 'function',
      description: [
        'Function toString converts arrays, numbers and booleans to strings',
        'Arguments:',
        'a: "*" Separates array by ", "',
        'a: "any-string" separates array elements.',
        'No effect over other data types.',
      ],
      arguments: '[toString=>a]' },
    { name: 'cast',
      type: 'function',
      description: [
        'Function cast convert one data type to another.',
        'Arguments:',
        'a: "string"',
        'a: "parseInt"',
        'a: "parseFloat"',
        'a: "boolean" "FALSE", "false", "0", 0, false, null or undefined return false',
      ],
      arguments: '[cast=>a' },
    { name: 'map',
      type: 'function',
      description: [
        'Function map converts array of objects into array of type-of "a".',
        'a: any-string',
      ],
      arguments: '[map=>a]' },
    { name: 'arrayToObject',
      type: 'function',
      description: [
        'Function arrayToObject converts [DataQuestion1, DataQuestion2,...] to { [DataQuestion1.key]: DataQuestion1.value, [DataQuestion2.key]: DataQuestion2.value, ...}',
        'Arguments:',
        'a: "any-string" key name',
        'b: "*" copies complete object into key',
        'b: "any-string" copy this value into key',
      ],
      arguments: '[arrayToObject=>a=b]' },
    { name: 'isDefinedFilter',
      type: 'function',
      description: [
        'Function isDefinedFilter filters an array of objects where the key exists.',
        'Arguments:',
        'a: "any-string" key name',
      ],
      arguments: '[isDefinedFilter=>a]' },
    { name: 'filter',
      type: 'function',
      description: [
        'Function filter filters an array of objects where the key value is equal to b.',
        'Arguments:',
        'a: "any-string" key name',
        'b: "any-string" value',
      ],
      arguments: '[filter=>a=b]' },
    { name: 'filterIndex',
      type: 'function',
      description: [
        'Function filterIndex returns an array with the indexes of the elements that are equal to b',
        'Arguments:',
        'a: "any-string" value to match'
      ],
      arguments: '[filterIndex=>a]' },
    { name: 'notEqualFilter',
      type: 'function',
      description: [
        'Function notEqualFilter filters an array of objects where the key "a" is NOT equal to "b"',
        'Arguments:',
        'a: "any-string" key to compare',
        'b: "any-string" value to compare',
      ],
      arguments: '[notEqualFilter=>a=b]' },
    { name: 'simpleNotEqualFilter',
      type: 'function',
      description: [
        'Function simpleNotEqualFilter filters an array where the value is NOT equal to "b".',
        'Arguments:',
        'a: "any-string" value to compare',
      ],
      arguments: '[simpleNotEqualFilter=>a]' },
    { name: 'simpleEqualFilter',
      type: 'function',
      description: [
        'Function simpleEqualFilter filters an array where the value is equal to "b".',
        'Arguments:',
        'a: "any-string" value to compare',
      ],
      arguments: '[simpleEqualFilter=>a]' },
    { name: 'concat',
      type: 'function',
      description: [
        'Function concat concatenates arrays.',
        'Arguments:',
        'a: "array" only argument',
        'b: "path-to-array" expression to array',
      ],
      arguments: '[concat=>a=b]' },
    { name: 'find',
      type: 'function',
      description: [
        'Function find return first object in array where key "a" is equal to "b"',
        'Arguments:',
        'a: "any-string" object key',
        'b: "any-string" value to compare',
      ],
      arguments: '[find=>a=b]' },
    { name: 'push',
      type: 'function',
      description: [
        'Function push adds value to end of array',
        'Arguments:',
        'a: "string" append value as string',
        'a: "number" append value as number (float)',
        'b: "any-string" value to append',
      ],
      arguments: '[push=>a=b]' },
    { name: 'add',
      type: 'function',
      description: [
        'Function add inserts value to end of array ONLY IF it doesn\'t exist in the array',
        'Arguments:',
        'a: "string" append value as string',
        'a: "number" append value as number (float)',
        'b: "any-string" value to append',
      ],
      arguments: '[add=>a=b]' },
    { name: 'math',
      type: 'function',
      description: [
        'Function math applies mathematical operations on numbers.',
        'Arguments:',
        'a: "add" add numeric value',
        'a: "subtract" subtract numeric value',
        'b: "any-numeric-string" right-hand-side of the operation',
      ],
      arguments: '[math=>a=b]' },
    { name: 'date',
      type: 'function',
      description: [
        'Function date converts formats',
        'Arguments:',
        'a: "format" format date',
        'b: "DATE-FORMAT" e.g., YYYY/MM/DD, HH:mm, etc',
        'b: "DATE-FORMAT@Timezone" e.g., YYYY/MM@America/Santiago',
      ],
      arguments: '[function=>a=b]' },
    { name: 'every',
      type: 'function',
      description: [
        'Function every return true if EVERY element in the array matches the comparator value',
        'Arguments: ',
        'a: "gte" greater than or equal',
        'a: "gt" greater than',
        'a: "lte" less than or equal',
        'a: "lt" less than',
        'a: "eq" equal',
        'b: "any-number" value to compare',
      ],
      arguments: '[every=>a=b]' },
    { name: 'some',
      type: 'function',
      description: [
        'Function every return true if ANY element in the array matches the comparator value',
        'Arguments: ',
        'a: "gte" greater than or equal',
        'a: "gt" greater than',
        'a: "lte" less than or equal',
        'a: "lt" less than',
        'a: "eq" equal',
        'b: "any-number" value to compare',
      ],
      arguments: '[some=>a=b]' },
    { name: 'json',
      type: 'function',
      description: [
        'Function json applies JSON operators over data or array of data.',
        'Arguments:',
        'a: "parse" convert string to Object/Array',
        'a: "stringify" converts objects into strings',
      ],
      arguments: '[json=>a]' },
    { name: 'querystring',
      type: 'function',
      description: [
        'Function querystring applies serialization/deserialization over query-params types',
        'Arguments:',
        'a: "escape" apply encodeURIComponent over value. function encodes a URI by replacing each instance of certain characters by one, two, three, or four escape sequences representing the UTF-8 encoding of the character (will only be four escape sequences for characters composed of two "surrogate" characters).',
        'a: "string" split string by & then by = and create an object.',
        'a: "object" | "any-string" apply querystring-stringify on object.',
      ],
      arguments: '[querystring=>a]' },
    { name: 'cotanswer_list',
      type: 'function',
      description: [
        'Function cotanswer_list is a specialized function to convert list survey-question responses into their display values',
        'Arguments:',
        'a: "array" default conversion',
      ],
      arguments: '[cotanswer_list]' },
    { name: 'gencode',
      type: 'function',
      description: [
        'Function gencode generates random code',
        'Arguments:',

      ],
      arguments: '[gencode=>a]' },
  ].map(x => {
    if (Array.isArray(x.description)) {
      x.description = (x.description.join('\n') as any);
    }
    return x;
  });

  ngOnInit() {
    this.subscribeToChanges();
    this.evaluateCotLang();
    this.parseExamples();
    this.loadContexts();
  }

  parseExamples() {
    const parser = new COTLangParser({ BASEURL: this.tokenService.api }, this.tokenService.api, '');
    this.examples.forEach((e, i) => {
      parser.resolve(e.input, JSON.parse(JSON.stringify(e.value || {})), e.output || []).then((x) => {
        e.result = x;
      });
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  submitCredentials() {
    if (this.emailForm.invalid) { return; }

    const { email, password } = this.emailForm.getRawValue();

    const subs = this.tokenService.getToken(email, password).subscribe((val) => {
      this.tokenForm.setValue(val.token);
    }, () => {
      this.snackBar.open('¡Usuario y/o contraseña incorrectos!', 'Ok', {
        duration: this.snackBarTime,
      });
    });
  }

  async evaluateCotLang() {
    const token = `Bearer ${this.tokenForm.value}`;

    if (this.cotLangForm.invalid) {
      console.log('cotLangForm invalid');
      this.snackBar.open(`Ingresar valor en los campos 'expression', 'value', y/o 'output'`, 'Ok', {
        duration: this.snackBarTime,
      });
      return;
    }

    const { expression, value: valueString, output: outputString } = this.cotLangForm.getRawValue();

    let value = {};
    let output = [];

    if (this.checkObject('value', valueString)) {
      value = JSON.parse(valueString);
    } else {
      return;
    }

    if (this.checkObject('output', outputString)) {
      output = JSON.parse(outputString);
    } else {
      return;
    }

    // TODO: apply cotLang things .....
    const parser = new COTLangParser({}, this.tokenService.api, token);
    try {
      console.log(`---------------------------\nEval: ${expression}\n---------------------------`);
      const res = await parser.resolve(expression, value, output);
      this.cotLangResponse = JSON.stringify(res, null, 2);

      const print = (node: IToken, depth: number = 0) => {
        if (!node) { return; }
        console.log(`[${String(depth).padStart(3, '0')}]${Array(depth).fill(null).join('-')}${node.text}`);
        node.children.forEach(n => print(n, depth + 1));
      };
      print(parser.parseOnly(expression));
    } catch (e) {
      this.cotLangResponse = `Error\n\n${e.message}`;
      console.log(e);
    }
    // this.cotLangResponse = `Resultado! :)... \n${expression}\n${JSON.stringify(value)}\n${JSON.stringify(output)}`;

  }

  private async loadContexts() {
    const langSupport = new COTLangSupport(null, null);
    // TODO Provide support for Contexts with auth-token to load surveys
    // const surveyId = '';
    try {
      this.contexts = [
        // {
        //   display: 'Channel Survey Trigger',
        //   value: JSON.stringify(await langSupport.channelSurveyTrigger(surveyId, true)),
        // },
        {
          display: 'Post Workflow Start',
          value: JSON.stringify(await langSupport.postWorkflowStart()),
        },
        {
          display: 'SLA',
          value: JSON.stringify(await langSupport.sla()),
        },
        // {
        //   display: 'State Survey Trigger',
        //   value: JSON.stringify(await langSupport.stateSurveyTrigger(surveyId)),
        // },
        {
          display: 'Changed State',
          value: JSON.stringify(await langSupport.changedState()),
        },
        {
          display: 'Schedule',
          value: JSON.stringify(await langSupport.schedule()),
        },
        {
          display: 'Slash Command Trigger',
          value: JSON.stringify(await langSupport.slashCommandTrigger('\\command sample text')),
        },
        // {
        //   display: 'Workflow Start',
        //   value: JSON.stringify(await langSupport.workflowStart(surveyId)),
        // },
      ];
    } catch (e) {
      console.log(e);
    }
    this.cd.markForCheck();
  }

  private checkObject(name: string, valueString: string): boolean {
    try {
      const value = JSON.parse(valueString);
      return true;
    } catch (e) {
      console.log('Parser error');
      this.snackBar.open(`Error en '${name}' con valor '${valueString}'..... No es posible parsear a objeto`, 'Ok', {
        duration: this.snackBarTime,
      });
      return false;
    }
  }

  private subscribeToChanges() {
    this.subscriptions.add(
      this.cotLangForm.get('context').valueChanges.subscribe((value) => {
        this.cotLangForm.get('value').setValue(value);
      }),
    );

    this.subscriptions.add(
      this.cotLangForm.valueChanges.subscribe((value) => {
        this.evaluateCotLang();
      }),
    );
  }
}
